import React, { useState } from 'react';
import { 
  QrCode, 
  Printer, 
  Download, 
  Eye, 
  Package,
  Barcode,
  Copy,
  Check,
  Smartphone,
  Camera,
  FileText
} from 'lucide-react';

const BarcodeIntegration = () => {
  const [selectedBatch, setSelectedBatch] = useState('NAL-2024-001');
  const [copied, setCopied] = useState('');
  const [showMobileGuide, setShowMobileGuide] = useState(false);

  const batches = [
    {
      batchNumber: 'NAL-2024-001',
      barcode: '3012345678901',
      qrCode: 'NAL-2024-001|500|2024-01-15|UMS',
      kitType: 'Take-Home Naloxone Kit',
      quantity: 500,
      createdDate: '2024-01-15'
    },
    {
      batchNumber: 'NAL-2024-002', 
      barcode: '3012345678902',
      qrCode: 'NAL-2024-002|200|2024-01-16|UMS',
      kitType: 'Training Naloxone Kit',
      quantity: 200,
      createdDate: '2024-01-16'
    },
    {
      batchNumber: 'NAL-2024-003',
      barcode: '3012345678903',
      qrCode: 'NAL-2024-003|100|2024-01-14|UMS',
      kitType: 'Professional Naloxone Kit',
      quantity: 100,
      createdDate: '2024-01-14'
    }
  ];

  const components = [
    {
      sku: 'NAL-001',
      name: 'Naloxone Nasal Spray 4mg',
      barcode: '8901234567890',
      lot: 'LOT-2024-001'
    },
    {
      sku: 'INST-002',
      name: 'Instruction Cards (EN/FR)',
      barcode: '8901234567891',
      lot: 'LOT-2024-002'
    },
    {
      sku: 'FACE-003',
      name: 'Face Shield/Barrier',
      barcode: '8901234567892',
      lot: 'LOT-2024-003'
    }
  ];

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(''), 2000);
  };

  const downloadPDFGuide = () => {
    // Create PDF content
    const pdfContent = `
# Unique Medical Supply - Barcode Integration Guide
# Naloxone Kit Manufacturing WMS System

## PHONE SCANNING - YES, IT WORKS!

Your phone/tablet CAN scan the barcodes and QR codes:

### Method 1: Built-in Camera (Easiest)
1. Open your phone's Camera app
2. Point at any QR code in the system
3. Your phone will show the batch information
4. Tap the notification to see full details
5. Works on iPhone and Android

### Method 2: Browser Scanner
1. Access the WMS system on your phone browser
2. Go to "Barcode Integration" tab
3. Click "Open Scanner" buttons
4. Allow camera access when prompted
5. Point camera at barcode/QR code
6. System automatically processes the scan

### Method 3: Barcode Scanner Apps
Download these FREE apps:
- QR & Barcode Scanner (by Gamma Play)
- Scanner Pro by Readdle
- Microsoft Lens (also does QR codes)

## AUTOMATIC BARCODE CREATION

The system automatically creates barcodes when you:
✓ Start a new batch (NAL-2024-001, etc.)
✓ Complete a batch
✓ Add new components
✓ Generate shipping labels

## BATCH BARCODE FORMAT

Format: 301234567890 (12 digits)
- 301 = Unique Medical Supply prefix
- 234567 = Batch identifier
- 890 = Check digits

## QR CODE DATA

Format: BatchNumber|Quantity|Date|Company
Example: NAL-2024-001|500|2024-01-15|UMS

## SCANNING WORKFLOW

Daily Production:
1. Start batch → System creates barcode
2. Print label → Apply to work area
3. Scan components → Track usage
4. Complete batch → Generate shipping barcode
5. Scan for verification → Ship out

## HARDWARE RECOMMENDATIONS

Handheld Scanners (Optional):
- Budget: Honeywell Voyager 1400g (~$150)
- Professional: Zebra DS2208 (~$250)

Label Printers (Optional):
- Budget: Brother QL-800 (~$100)
- Professional: Zebra GX420d (~$300)

## TROUBLESHOOTING

Phone won't scan?
→ Ensure good lighting
→ Hold steady 6-12 inches away
→ Try different camera angle
→ Clean camera lens

Barcode not clear?
→ Print at higher quality
→ Increase label size
→ Check printer settings

System not responding?
→ Click in text field first
→ Check camera permissions
→ Refresh page and retry

## MOBILE ACCESS

The entire WMS system works on phones/tablets:
1. Open browser (Chrome, Safari, etc.)
2. Go to your system URL
3. Login with your credentials
4. Full functionality available
5. Touch-friendly interface

## CONTACT SUPPORT

For technical help:
- Login issues: Check credentials
- Scanner problems: Check camera permissions
- Printing issues: Check printer connections
- General questions: Contact system administrator

© 2024 Unique Medical Supply Ltd.
    `;

    // Create and download file
    const element = document.createElement('a');
    const file = new Blob([pdfContent], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'Unique_Medical_Barcode_Guide.txt';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Barcode Integration System</h1>
        <p className="text-gray-600">Generate and manage barcodes for batches and components</p>
        
        {/* Download Guide Button */}
        <div className="mt-4 flex space-x-4">
          <button 
            onClick={downloadPDFGuide}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
          >
            <Download className="h-4 w-4" />
            <span>Download Setup Guide</span>
          </button>
          <button 
            onClick={() => setShowMobileGuide(!showMobileGuide)}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
          >
            <Smartphone className="h-4 w-4" />
            <span>Phone Scanning Guide</span>
          </button>
        </div>
      </div>

      {/* Mobile Scanning Guide */}
      {showMobileGuide && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <Smartphone className="h-5 w-5 text-green-600 mr-2" />
            YES! Your Phone CAN Scan Barcodes
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4 border border-green-200">
              <Camera className="h-8 w-8 text-green-600 mb-2" />
              <h3 className="font-medium text-gray-900 mb-2">Built-in Camera</h3>
              <p className="text-sm text-gray-600">
                Open Camera app → Point at QR code → Tap notification to see batch details
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-4 border border-green-200">
              <QrCode className="h-8 w-8 text-green-600 mb-2" />
              <h3 className="font-medium text-gray-900 mb-2">Browser Scanner</h3>
              <p className="text-sm text-gray-600">
                Use WMS on phone → Click "Open Scanner" → Allow camera → Scan automatically
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-4 border border-green-200">
              <FileText className="h-8 w-8 text-green-600 mb-2" />
              <h3 className="font-medium text-gray-900 mb-2">Scanner Apps</h3>
              <p className="text-sm text-gray-600">
                Download free QR scanner apps → Scan any QR code → Get batch information
              </p>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-green-100 rounded-lg">
            <p className="text-sm text-green-800">
              <strong>Best Practice:</strong> Hold phone 6-12 inches from QR code in good lighting. 
              Works on iPhone, Android, and tablets!
            </p>
          </div>
        </div>
      )}

      {/* Batch Barcode Generation */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Batch Selection */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <Package className="h-5 w-5 text-red-600 mr-2" />
            Batch Barcodes (Auto-Generated)
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Batch</label>
              <select
                value={selectedBatch}
                onChange={(e) => setSelectedBatch(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                {batches.map(batch => (
                  <option key={batch.batchNumber} value={batch.batchNumber}>
                    {batch.batchNumber} - {batch.kitType}
                  </option>
                ))}
              </select>
            </div>

            {batches.filter(b => b.batchNumber === selectedBatch).map(batch => (
              <div key={batch.batchNumber} className="space-y-4">
                {/* Barcode Display */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">UPC Barcode</label>
                    <button
                      onClick={() => copyToClipboard(batch.barcode, 'barcode')}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      {copied === 'barcode' ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </div>
                  <div className="bg-white border-2 border-dashed border-gray-300 rounded p-4 text-center">
                    <Barcode className="h-12 w-24 mx-auto text-gray-400 mb-2" />
                    <p className="font-mono text-lg">{batch.barcode}</p>
                    <p className="text-xs text-gray-500 mt-1">UPC-A Format</p>
                  </div>
                </div>

                {/* QR Code Display */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">QR Code (Phone Scannable)</label>
                    <button
                      onClick={() => copyToClipboard(batch.qrCode, 'qrcode')}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      {copied === 'qrcode' ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </div>
                  <div className="bg-white border-2 border-dashed border-gray-300 rounded p-4 text-center">
                    <QrCode className="h-16 w-16 mx-auto text-gray-400 mb-2" />
                    <p className="font-mono text-sm break-all">{batch.qrCode}</p>
                    <p className="text-xs text-green-600 mt-1">📱 Scan with Phone Camera!</p>
                  </div>
                </div>

                {/* Print Actions */}
                <div className="flex space-x-3">
                  <button className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg flex items-center justify-center space-x-2">
                    <Printer className="h-4 w-4" />
                    <span>Print Labels</span>
                  </button>
                  <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg flex items-center justify-center space-x-2">
                    <Download className="h-4 w-4" />
                    <span>Download</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Component Barcodes */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <Barcode className="h-5 w-5 text-blue-600 mr-2" />
            Component Barcodes
          </h2>

          <div className="space-y-4">
            {components.map((component, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-medium text-gray-900">{component.name}</p>
                    <p className="text-sm text-gray-600">SKU: {component.sku} | Lot: {component.lot}</p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(component.barcode, `component-${index}`)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    {copied === `component-${index}` ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
                <div className="bg-gray-50 rounded p-2 text-center">
                  <Barcode className="h-6 w-12 mx-auto text-gray-400 mb-1" />
                  <p className="font-mono text-sm">{component.barcode}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Barcode Scanner Integration */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <Eye className="h-5 w-5 text-green-600 mr-2" />
          Scanning Options (Multiple Ways)
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <QrCode className="h-12 w-12 text-blue-600 mx-auto mb-3" />
            <h3 className="font-medium text-gray-900 mb-2">Batch Scanning</h3>
            <p className="text-sm text-gray-600 mb-3">Scan batch QR codes for instant lookup</p>
            <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm">
              Open Camera Scanner
            </button>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
            <Smartphone className="h-12 w-12 text-green-600 mx-auto mb-3" />
            <h3 className="font-medium text-gray-900 mb-2">Phone Camera</h3>
            <p className="text-sm text-gray-600 mb-3">Use your phone's built-in camera app</p>
            <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm">
              Phone Instructions
            </button>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
            <Barcode className="h-12 w-12 text-purple-600 mx-auto mb-3" />
            <h3 className="font-medium text-gray-900 mb-2">Handheld Scanner</h3>
            <p className="text-sm text-gray-600 mb-3">Professional barcode scanners</p>
            <button className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded text-sm">
              Hardware Guide
            </button>
          </div>
        </div>
      </div>

      {/* Simple Instructions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">How It Works (Simple)</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-medium text-gray-900 mb-3 text-green-600">✓ AUTOMATIC: System Creates Barcodes</h3>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• When you start a batch → Barcode created automatically</li>
              <li>• When you complete a batch → Shipping barcode ready</li>
              <li>• When components arrive → Barcodes assigned</li>
              <li>• No manual barcode creation needed!</li>
            </ul>
          </div>

          <div>
            <h3 className="font-medium text-gray-900 mb-3 text-blue-600">📱 PHONE SCANNING: Super Easy</h3>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• Open phone camera → Point at QR code</li>
              <li>• Phone shows batch info automatically</li>
              <li>• Works on any iPhone or Android</li>
              <li>• No special apps needed!</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BarcodeIntegration;